import type {
  AlertOccurrence,
  GroupedAlert,
} from '@kirin/api-client/contracts/alerts';

/**
 * KIR-4281 — projects one {@link AlertOccurrence} onto the {@link GroupedAlert}
 * shape the detail panel consumes, narrowing the plural fields (`users`,
 * `clientTypes`, `securityGroups`) to this occurrence's single value.
 *
 * Two rules the tests pin down, since either one wrong reintroduces the bug:
 *
 *  1. **No evidence is inherited from `parent`.** Its `chatExcerpt` /
 *     `commandExcerpt` / `toolCallScan` / `location` are representative values
 *     from the LATEST occurrence, possibly another user's — the KIR-4212
 *     mis-attribution. An occurrence with none renders "unavailable".
 *  2. **No group-level state is carried over** (`mute`, `exclude`, `feedback`,
 *     `severityOverride`). The panel also needs `variant="occurrence"`: an
 *     absent `mute` renders a live *Mute* button, not no button.
 *
 * `alertKey` is inherited only because the type requires it; the caller does
 * not pass it as the panel's `alertKey` prop, so it drives nothing.
 */
export function occurrenceToDetailAlert(
  occurrence: AlertOccurrence,
  parent: GroupedAlert
): GroupedAlert {
  return {
    // Required by the type, inert in the panel.
    alertKey: parent.alertKey,

    type: occurrence.type,
    // Raw stored severity; the grouped value is the EFFECTIVE one (override
    // applied), so the two can differ. This matches the row's Severity cell.
    severity: occurrence.severity,
    description: occurrence.description,
    // Raw `findings.location` where the group ships the normalized form.
    // `finding-identity.ts` keeps the raw one "for the occurrence view": it
    // retains the `Why flagged:` block and path prefix that grouping strips.
    location: occurrence.location ?? null,
    filePath: occurrence.filePath,

    // One event, one timestamp.
    firstSeenAt: occurrence.scannedAt,
    lastSeenAt: occurrence.scannedAt,

    users: [
      {
        ...occurrence.user,
        occurrenceCount: 1,
        lastSeenAt: occurrence.scannedAt,
      },
    ],

    // Transcript path uses this occurrence's own client. A null client stays
    // null so the detail model does not fall back to another IDE.
    ...(occurrence.clientType
      ? {
          clientTypes: [occurrence.clientType],
          clientType: occurrence.clientType,
        }
      : occurrence.clientType === null
        ? { clientType: null }
        : {}),

    /*
      Three states that must not collapse (same guard as the table's own
      Security Group cell): undefined = flag off, render nothing; null = user in
      no group, renders the organization scope; {...} = that user's group.
    */
    ...(occurrence.securityGroup === undefined
      ? {}
      : {
          securityGroups: occurrence.securityGroup
            ? [occurrence.securityGroup]
            : [],
        }),

    /*
      `endpoints` omitted, not synthesized: the grouped schema is a full
      endpoint record and an occurrence has only an id and name, so the rest
      would be invented — the caller passes the label to the facts strip.
      `occurrenceCount` omitted likewise: it renders as the instance count on
      Show Instances, which reads as the group's count. The occurrences page
      passes no `onShowOccurrences`, so that button never renders here.
    */

    ...(occurrence.score !== undefined ? { score: occurrence.score } : {}),
    ...(occurrence.outcome !== undefined
      ? { outcome: occurrence.outcome }
      : {}),
    ...(occurrence.subPolicyKey !== undefined
      ? { subPolicyKey: occurrence.subPolicyKey }
      : {}),
    // Group-level taxonomy, correct for the occurrence too.
    ...(parent.policyKey !== undefined ? { policyKey: parent.policyKey } : {}),

    // Already authoritative per occurrence.
    ...(occurrence.sourceDetails !== undefined
      ? { sourceDetails: occurrence.sourceDetails }
      : {}),
    ...(occurrence.scoreBreakdown !== undefined
      ? { scoreBreakdown: occurrence.scoreBreakdown }
      : {}),
    ...(occurrence.project !== undefined
      ? { project: occurrence.project }
      : {}),
    ...(occurrence.engine !== undefined ? { engine: occurrence.engine } : {}),
    ...(occurrence.ruleId !== undefined ? { ruleId: occurrence.ruleId } : {}),
    ...(occurrence.ruleName !== undefined
      ? { ruleName: occurrence.ruleName }
      : {}),
    ...(occurrence.entropy !== undefined
      ? { entropy: occurrence.entropy }
      : {}),
    ...(occurrence.toolName !== undefined
      ? { toolName: occurrence.toolName }
      : {}),
    ...(occurrence.sessionId !== undefined
      ? { sessionId: occurrence.sessionId }
      : {}),
    // This occurrence's own session, keyed on its own id — the parent's link
    // points at the LATEST occurrence (rule 1).
    ...(occurrence.id && occurrence.sessionLink
      ? {
          sessionLink: {
            occurrenceId: occurrence.id,
            ...occurrence.sessionLink,
          },
        }
      : {}),
    ...(occurrence.workspaceName !== undefined
      ? { workspaceName: occurrence.workspaceName }
      : {}),
    ...(occurrence.eventKind !== undefined
      ? { eventKind: occurrence.eventKind }
      : {}),
    ...(occurrence.matchContext !== undefined
      ? { matchContext: occurrence.matchContext }
      : {}),
    ...(occurrence.detectionPattern !== undefined
      ? { detectionPattern: occurrence.detectionPattern }
      : {}),
    ...(occurrence.isAIGenerated !== undefined
      ? { isAIGenerated: occurrence.isAIGenerated }
      : {}),

    // This occurrence's own evidence, never the parent's. See rule 1.
    ...(occurrence.chatExcerpt !== undefined
      ? { chatExcerpt: occurrence.chatExcerpt }
      : {}),
    ...(occurrence.commandExcerpt !== undefined
      ? { commandExcerpt: occurrence.commandExcerpt }
      : {}),
    ...(occurrence.toolCallScan !== undefined
      ? { toolCallScan: occurrence.toolCallScan }
      : {}),
  };
}
